import React, { useEffect, useState } from "react";
import config from "clientappsetting.json";
import { Checkbox, CheckboxGroup } from "components/generic/CheckboxGroup";
import { Button } from "react-bootstrap";
import { Hero } from "components/generic/Hero";
import * as yup from "yup";
import { Form as FormikForm, Formik, Field, ErrorMessage } from "formik";
import { FormikFormRow } from "components/generic/FormRow";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { scrollToTop } from "helpers/ScrollingHelper";
import { ContactPreferenceType, WorkDetails } from "types/NewBooking";
import { DriverDetails } from "generated/types/types";
import { ManagedFormikField } from "components/generic/ManagedFormikField";
import { useTranslation } from "react-i18next";
import { ApiUrlResolver } from "utils/ApiUrlResolver";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";


type FormValues = {
    driverName: string;
    contactNumber: string;
    mobileNumber?: string;
    emailAddress: string;
    confirmEmailAddress: string;
    contactPreferences: ContactPreferenceType[] | null;
    hasReadPrivacyPolicy: boolean;
    isBookingOnBehalf: boolean; 
    bookerName?: string;  
};

interface Props extends INewBookingPropsBase {
    encryptedSchemeId: string;
    privacyPolicyUrl: string;
    currentDriverDetails: DriverDetails | null;
    setDriverDetails: (value: DriverDetails) => void;
    currentWorkDetails: WorkDetails;
    serviceDeskBookerName: boolean;
}

export const DriverDetailsInput: React.FC<Props> = (props: Props) => {
    const [schemeBookingAcceptanceSms, setSchemeBookingAcceptanceSms] = useState<boolean>(false);
    const { t, i18n } = useTranslation();
    const countryCode = useSelector((state: RootState) => state.country.countryCode);

    const lang = i18n.language?.toLowerCase() ?? "";
    const langIsDanish = lang.startsWith("da") || lang.startsWith("dk");
    const effectiveCountry = (("countryCode" in props && (props as any).countryCode) ||
                            (langIsDanish ? "DK" : "UK")) as "UK" | "DK";

    const UK_SMS_REGEX = /^(?:07\d{9}|\+447\d{9})$/;   // 07 + 9 digits OR +447 + 9 digits
    const DK_SMS_REGEX = /^\+45\d{8}$/;                // "+45" + 8 digits, exactly

    const isValidSimplePhone = (s: string | null | undefined) => {
    const n = (s ?? "").replace(/\D/g, "").length;
    return n >= 8 && n <= 15;
    };

    const schema = yup.object({
        contactPreferences: yup.array().required(t("driver_details_select_option")),
        emailAddress: yup
            .string()
            .matches(/^[A-Z0-9.!#$%&'*+-/=?^_`{|}~]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, t("driver_details_valid_email"))
            .required(t("driver_details_email_required"))
            .max(150, t("driver_details_valid_email")),
        confirmEmailAddress: yup
            .string()
            .matches(/^[A-Z0-9.!#$%&'*+-/=?^_`{|}~]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, t("driver_details_valid_email"))
            .required(t("driver_details_email_required"))
            .oneOf([yup.ref("emailAddress")], t("driver_details_emails_match"))
            .max(150, t("driver_details_valid_email")),
        driverName: yup.string().required(t("driver_details_name")).max(50, t("driver_details_name_50_chars")),
        contactNumber: yup
            .string()
            .matches(/^[+0-9\s().-]{7,20}$/, t("driver_details_not_valid"))
            .test("simple-phone", t("driver_details_not_valid"), (v) => isValidSimplePhone(v))
            .required(t("driver_details_telephone_required")),
        mobileNumber: yup
            .string()
            .nullable()
            .transform((v) => (v === "" ? null : v))
            .when("contactPreferences", {
                is: (x: ContactPreferenceType[] | null) => x?.includes(ContactPreferenceType.Sms),
                 then: yup
                .string()
                .required(t("driver_details_mobile_required"))
                .test(
                    "country-locked-format",
                    effectiveCountry === "DK"
                        ? t("driver_details_not_valid")
                        : t("driver_details_sms_uk_not_valid"),
                    (v) => {
                        if (!v) return false;
                        const raw = v.replace(/\s+/g, "");
                        return effectiveCountry === "DK"
                            ? DK_SMS_REGEX.test(raw)
                            : UK_SMS_REGEX.test(raw);
                    }
                ),
                otherwise: yup.string().nullable(),
            }),

        hasReadPrivacyPolicy: yup.bool().oneOf([true], t("driver_details_tick_box")),
        bookerName: yup.string().when("isBookingOnBehalf", {
            is: true, 
            then: yup
                .string()
                .required(t("driver_details_behalf_driver"))
                .max(50, t("driver_details_name_50_chars")),
            otherwise: yup.string().nullable(),
        }),
    });

    useEffect(() => {
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + `/DriverMicrosite/SchemeBookingAcceptanceSmsEnabled/${props.encryptedSchemeId}`, { method: "GET" })
            .then(response => response.json())
            .then((data: boolean) => {
                setSchemeBookingAcceptanceSms(data);
            });
    }, []);
    useEffect(() => scrollToTop(), []);

    // if the collection name has been entered on the work required page use that to prefill
    const driverName = props.currentDriverDetails?.driverName ?? props.currentWorkDetails?.collectionContactName;
    const contactNumber = props.currentDriverDetails?.phoneNumber;
    const bookerName = props.currentDriverDetails?.bookerName ?? "";  


    const contactOptions: Checkbox<ContactPreferenceType>[] = [
        ...(schemeBookingAcceptanceSms
            ? [
                {
                    value: ContactPreferenceType.Sms,
                    label: t("driver_details_sms"),
                },
            ]
            : []),
    ];

    const initialValues: FormValues = {
        driverName: driverName ?? "",
        contactNumber: contactNumber ?? "",
        emailAddress: props.currentDriverDetails?.email ?? "",
        confirmEmailAddress: props.currentDriverDetails?.email ?? "",
        // Email is always added as default since it's required for booking confirmations etc
        contactPreferences: [ContactPreferenceType.Email],
        hasReadPrivacyPolicy: false,
        isBookingOnBehalf: props.currentDriverDetails?.isBookingOnBehalf ?? false,
        bookerName: props.currentDriverDetails?.bookerName ?? "" 
    };

    const handleSubmit = (values: FormValues, { setSubmitting }: { setSubmitting: (x: boolean) => void }) => {

        const normSms = (() => {
            if (!values.mobileNumber) return null;
            const raw = values.mobileNumber.replace(/\s+/g, "");
            if (effectiveCountry === "DK") {
                return raw; // "+45########"
            } else {
                if (/^0\d{10}$/.test(raw)) return "+44" + raw.slice(1);
                if (/^\+44\d{10}$/.test(raw)) return raw;
                return raw;
            }
        })();

        props.setDriverDetails({
            driverName: values.driverName,
            email: values.emailAddress,
            phoneNumber: values.contactNumber,
            smsMobileNo: normSms,
            contactPreferences: [...(values.contactPreferences ?? [])],
            bookerName: values.bookerName ?? "",
            isBookingOnBehalf: values.isBookingOnBehalf
        });
        setSubmitting(false);
        props.goToNextStep();
    };

    const SmsAutofillEffect: React.FC<{
        values: FormValues;
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
        effectiveCountry: "UK" | "DK";
    }> = ({ values, setFieldValue, effectiveCountry }) => {
        React.useEffect(() => {
            const wantsSms = values.contactPreferences?.includes(ContactPreferenceType.Sms);

            if (wantsSms && effectiveCountry === "DK") {
            const current = (values.mobileNumber ?? "").replace(/\s+/g, "");
            if (!current || !current.startsWith("+45")) {
                setFieldValue("mobileNumber", "+45");
            }
        }
    }, [values.contactPreferences, values.mobileNumber, effectiveCountry, setFieldValue]);

    return null;
    };

    return (
        <>
            <Hero heading={t("driver_details_driver_details")} description={t("driver_details_contact_details")}></Hero>
            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                {({ values, isSubmitting, setFieldValue }) => (
                <div>

                    <SmsAutofillEffect
                        values={values}
                        setFieldValue={setFieldValue}
                        effectiveCountry={effectiveCountry}
                    />

                        <FormikForm>
                            <div className="form-group">
                                <FormikFormRow name="driverName" label={t("driver_details_driver_name")} type="text" />
                                <div className="row input-row">
                                    <div className="col-sm-12">
                                        <ManagedFormikField
                                            inputClassName="large-input"
                                            name="contactNumber"
                                            label={t("driver_details_telephone")}
                                            type="tel"
                                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                                const raw = e.currentTarget.value ?? "";
                                                const normalised = raw.replace(/\s+/g, "");
                                                setFieldValue("contactNumber", normalised);
                                            }}
                                        />
                                    </div>
                                </div>
                                <FormikFormRow name="emailAddress" label={t("driver_details_email")} type="email" />
                                <FormikFormRow name="confirmEmailAddress" label={t("driver_details_confirm_email")} type="email" />
                                {props.serviceDeskBookerName && (
                                    <>
                                        <div className="checkbox-input">
                                            <Field
                                                type="checkbox"
                                                name="isBookingOnBehalf"
                                                id="isBookingOnBehalf"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    const isChecked = e.target.checked;
                                                    setFieldValue("isBookingOnBehalf", isChecked);
                                                    if (!isChecked) {
                                                        setFieldValue("bookerName", "");
                                                    }
                                                }}
                                            />
                                            <label htmlFor="isBookingOnBehalf">
                                                {t("driver_details_behalf_driver_desc")}
                                            </label>
                                        </div>

                                        {values.isBookingOnBehalf && (
                                            <div className="form-group">
                                                <FormikFormRow name="bookerName" label={t("driver_details_enter_name")} type="text" />
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="col-form-label">{t("driver_details_booking_updates")}</div>

                                <>
                                    {schemeBookingAcceptanceSms ? (
                                        <>
                                            <label htmlFor="contactPreferences" className="col-form-label">
                                                {t("driver_details_sms_detail")}
                                            </label>

                                            <CheckboxGroup
                                                name="contactPreferences"
                                                className="checkbox-pref"
                                                checkboxes={contactOptions}
                                                selectedValues={values.contactPreferences}
                                            />
                                            {values.contactPreferences?.includes(ContactPreferenceType.Sms) ? (
                                                <ManagedFormikField
                                                    name="mobileNumber"
                                                    label={t("driver_details_mobile")}
                                                    type="text"
                                                    onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                                        const input = e.currentTarget.value ?? "";

                                                        if (effectiveCountry === "DK") {
                                                            let normalised = input.replace(/\s+/g, "");

                                                            if (!normalised.startsWith("+45")) {
                                                                normalised = normalised.replace(/^\+?\d+/, "");
                                                                normalised = "+45" + normalised;
                                                            }

                                                            normalised = normalised.slice(0, 3 + 8); 
                                                            setFieldValue("mobileNumber", normalised);
                                                            return;
                                                        }

                                                        setFieldValue("mobileNumber", input);
                                                    }}
                                                    />
                                            ) : (
                                                <></>
                                            )}
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </>

                                <div className="checkbox-input privacy-policy" key={"privacyPolicy"}>
                                    <Field type="checkbox" name="hasReadPrivacyPolicy" id="hasReadPrivacyPolicy" />

                                    <label htmlFor="hasReadPrivacyPolicy">
                                        {t("driver_details_policy_1")}&nbsp;
                                        <a target="_blank" rel="noopener noreferrer" href={props.privacyPolicyUrl}>
                                            {t("driver_details_policy_2")}
                                        </a>
                                    </label>

                                    <div className="col-sm-12">
                                        <ErrorMessage name="hasReadPrivacyPolicy" component="div" className="error-msg" />
                                    </div>
                                </div>
                            </div>
                            <Button type="submit" className="large-input" disabled={isSubmitting}>
                                {t("driver_details_submit")}
                            </Button>
                        </FormikForm>
                    </div>
                )}
            </Formik>
        </>
    );
};
